// Waveform mipmaps (P2.4) — Architecture §5 stage 1, the only analysis stage M2 wants.
//
// The problem this exists to solve is stated in Architecture §4.4: **"waveform rendering never
// touches PCM at draw time."** A 6-minute stereo track is ~17 million frames per channel. Drawing
// a 1880-pixel ribbon straight from that would mean reading ~9,000 samples per pixel, sixty times
// a second, on a thread that also has to stay out of the audio engine's way. It is not a matter of
// optimising that loop; it is the wrong shape of work to do at draw time at all.
//
// So the shape of the track is precomputed **once**, at load, into summaries dense enough to draw
// from directly. For each bucket of N frames we keep three numbers:
//
//   * **min** and **max** — the extremes. These are what makes a waveform look like a waveform:
//     the envelope, the transients, the moment the kick lands. Averaging would erase exactly the
//     features a DJ is looking at.
//   * **RMS** — the energy. Perceived loudness, which min/max alone misrepresents: a single stray
//     sample peak and a wall of sustained bass have the same min/max and very different RMS.
//     Drawing both is what gives the familiar "bright core inside a dimmer envelope" look.
//
// Same file shape as `track-memory.ts` and for the same reasons: written once, completely, before
// anything reads it; then read randomly forever. A header plus an array, closer to a file format
// than to a channel — which is also what makes Architecture §5's "waveform blobs in OPFS" a
// straight byte copy when the M3 analysis cache lands rather than a serialisation problem.
//
// Stages 2–5 (loudness, onsets, tempo, key) are M3. Nothing here anticipates them beyond leaving
// the header room to grow.

import { asByteOffset, type ByteOffset } from './branded';

/** 'SYWM' — SYnapse Waveform Mipmap. Distinct from TRACK_MEMORY_MAGIC and RING_MAGIC so three
 * buffer shapes that all arrive as `SharedArrayBuffer` can never be silently confused. */
export const WAVEFORM_MIPMAP_MAGIC = 0x53_59_57_4d;

export const WAVEFORM_MIPMAP_VERSION = 1;

/**
 * One bucket: three floats, interleaved, in this order.
 *
 * **Interleaved rather than three planar arrays, and that is the "laid out for GPU upload" part
 * of the task.** A bucket is one texel: `min` → R, `max` → G, `rms` → B, uploaded in a single
 * `texImage2D` with no repacking. Planar would be three uploads, or one upload plus a shuffle
 * pass on the CPU — both of which are work at draw time, which is the thing this file exists to
 * abolish. It is also the better cache order for the CPU fallback, since drawing one pixel column
 * touches all three values of one bucket and none of the neighbours'.
 */
export const WAVEFORM_BUCKET = {
  /** f32 — most negative sample in the bucket. */
  MIN: 0,
  /** f32 — most positive sample in the bucket. */
  MAX: 4,
  /** f32 — root-mean-square of the bucket, always ≥ 0. */
  RMS: 8,
} as const;

export const WAVEFORM_BUCKET_BYTES = 12;

/** Floats per bucket. Named because `12 / 4` appears in both the writer and the reader. */
export const WAVEFORM_BUCKET_FLOATS = 3;

/**
 * The zoom ladder: frames per bucket at each level, finest first.
 *
 * **Derived from what the UI actually draws, per P2.4's rule that this must not be a round
 * number.** The arithmetic, so the next person can check it rather than trust it:
 *
 * _The finest level (32)._ PRD §6.1's reference machine has a **1080p** display, so the deck's
 * scrolling ribbon is ~1880 px wide after chrome. The tightest zoom M2 offers shows about **2
 * seconds** across it — 96,000 frames at 48 kHz, or **≈51 frames per pixel**. A bucket larger
 * than that spans more than one pixel and the waveform goes visibly blocky at max zoom, so the
 * ceiling is 51 and the largest power of two under it is **32**. (64 would have been the tidy
 * answer and is the wrong one by nine frames.)
 *
 * _The step (4×)._ Doubling would give eleven levels between the ends instead of five, for a
 * quality difference nobody can see: with 4× steps the reader never blends more than four buckets
 * into one pixel, and four-into-one is already indistinguishable from exact at these sizes. The
 * step also sets the memory tail — see `totalBytes` below.
 *
 * _The coarsest level (32768)._ UX §3.1 puts two overview strips side by side, so each is ~720 px
 * and must show a **whole track** at once. The longest thing worth serving well is a recorded set
 * rather than NFR-P5's 8-minute track — call it 20 minutes, 57.6M frames — which needs buckets no
 * larger than 57.6M / 720 ≈ 80,000. 32768 clears that with 1,758 buckets to spare, and stops
 * there because a seventh level would save 1.6 kB.
 *
 * Short tracks are not a special case: nothing forces the overview to read the *coarsest* level,
 * only a level with at least one bucket per pixel, which `levelForFramesPerPixel` picks. A
 * 30-second jingle draws its overview from level 2 and never touches levels 4 or 5.
 */
export const WAVEFORM_MIPMAP_LADDER = [32, 128, 512, 2048, 8192, 32768] as const;

export const WAVEFORM_LEVEL_COUNT = WAVEFORM_MIPMAP_LADDER.length;

/**
 * Header fields. One cache line, like `TRACK_MEMORY_HEADER`, with room to grow.
 *
 * Two of these are written **after** readers may already be looking at the buffer, and are
 * therefore the only atomics in the file. Everything else is fixed before the buffer is shared.
 */
export const WAVEFORM_MIPMAP_HEADER = {
  /** u32 — WAVEFORM_MIPMAP_MAGIC. */
  MAGIC: 0,
  /** u32 — WAVEFORM_MIPMAP_VERSION. A ladder change bumps this; stale OPFS blobs then fail
   * validation instead of drawing a waveform at the wrong scale, which would look plausible. */
  VERSION: 4,
  /** u32 — frames per channel in the source track. The mipmap's own frame count, not the file's. */
  FRAME_COUNT: 8,
  /** u32 — sample rate at decode time. Carried so a reader can convert buckets to seconds without
   * being handed the track memory as well (P0.5: never assume 48 kHz). */
  SAMPLE_RATE_HZ: 12,
  /** u32 — `WAVEFORM_LEVEL_COUNT` at write time. Redundant with the ladder constant on purpose:
   * a blob from the M3 cache must be able to say how many levels it actually contains. */
  LEVEL_COUNT: 16,
  /**
   * u32, **atomic** — bitmask of complete levels; bit _n_ set means level _n_ is safe to read.
   *
   * The release half of a release/acquire pair, and the reason a reader may look at this buffer
   * while the worker is still filling it. The worker writes a level's floats, then
   * `Atomics.store`s its bit; a reader `Atomics.load`s the mask before touching any float. The
   * ordering guarantee is what makes "draw the overview while the fine levels are still
   * computing" safe rather than a data race that happens to work.
   */
  LEVELS_READY: 20,
  /**
   * u32, **atomic** — frames folded into the summaries so far, for a progress readout.
   *
   * Not used by P2.4 and not speculative either: UX §6 asks for "a thin progress line along the
   * overview strip" during load, which is P2.5's, and this is the number it needs. Placed now
   * because adding a header field later moves every payload byte after it.
   */
  FRAMES_DONE: 24,
} as const;

export const WAVEFORM_MIPMAP_HEADER_BYTES = 64;

export interface WaveformMipmapSpec {
  readonly frameCount: number;
  readonly sampleRateHz: number;
}

export interface WaveformMipmapLevel {
  readonly index: number;
  /** Frames summarised by one bucket at this level. */
  readonly bucketFrames: number;
  /** Buckets at this level. The last one may cover fewer than `bucketFrames` frames. */
  readonly bucketCount: number;
  readonly byteOffset: ByteOffset;
  readonly byteLength: number;
}

export interface WaveformMipmapLayout extends WaveformMipmapSpec {
  readonly headerBytes: number;
  readonly levels: readonly WaveformMipmapLevel[];
  readonly totalBytes: number;
}

/**
 * Validates a spec and derives its byte layout.
 *
 * Same shape as `computeTrackMemoryLayout` and `computeRingLayout` before it: called once, off
 * every hot path, so it is free to be thorough. Every throw here is a bug that would otherwise be
 * a waveform drawn from whatever bytes happened to be adjacent.
 */
export function computeWaveformMipmapLayout(
  spec: WaveformMipmapSpec,
): WaveformMipmapLayout {
  const { frameCount, sampleRateHz } = spec;

  if (!Number.isInteger(frameCount) || frameCount < 0) {
    throw new RangeError(
      `waveform mipmap: frameCount must be a non-negative integer, got ${String(frameCount)}`,
    );
  }
  if (!Number.isFinite(sampleRateHz) || sampleRateHz <= 0) {
    throw new RangeError(
      `waveform mipmap: sampleRateHz must be positive, got ${String(sampleRateHz)}`,
    );
  }

  const levels: WaveformMipmapLevel[] = [];
  let byteOffset = WAVEFORM_MIPMAP_HEADER_BYTES;

  for (const [index, bucketFrames] of WAVEFORM_MIPMAP_LADDER.entries()) {
    // `ceil`, not `floor`: the tail of a track is a partial bucket and dropping it would lose up
    // to 0.68 s at the coarsest level — a track that visibly ends before its own end.
    const bucketCount = Math.ceil(frameCount / bucketFrames);
    const byteLength = bucketCount * WAVEFORM_BUCKET_BYTES;

    levels.push({
      index,
      bucketFrames,
      bucketCount,
      byteOffset: asByteOffset(byteOffset),
      byteLength,
    });

    byteOffset += byteLength;
  }

  return {
    frameCount,
    sampleRateHz,
    headerBytes: WAVEFORM_MIPMAP_HEADER_BYTES,
    levels,
    // ≈ 0.5 bytes per frame all in (12 bytes per 32 frames, times 4/3 for the geometric tail of
    // coarser levels). A 6-minute track costs ~8.6 MB against the ~138 MB its own Float32 PCM
    // occupies — 6%, which is why this file spends memory freely to buy draw-time silence.
    totalBytes: byteOffset,
  };
}

/**
 * Byte offset of one bucket. The single place bucket addressing is expressed.
 *
 * No bounds check, deliberately, matching `slotByteOffset` and `trackChannelByteOffset`: this is
 * called per pixel column by the renderer, and a branch per column to catch a bug that
 * `computeWaveformMipmapLayout` already prevents is a cost paid forever for a mistake made once.
 */
export function bucketByteOffset(level: WaveformMipmapLevel, bucket: number): ByteOffset {
  return asByteOffset(level.byteOffset + bucket * WAVEFORM_BUCKET_BYTES);
}

/**
 * How many frames bucket `bucket` actually covers — `bucketFrames`, except for the last one.
 *
 * Load-bearing rather than pedantic. The coarser levels are folded from the finer ones (see
 * `mipmap.ts`), and folding RMS means recombining sums of squares, which needs each child's true
 * weight. Weighting a half-full final bucket as though it were full pulls the last bucket of
 * every coarser level toward silence — a track whose waveform fades out slightly at the end, on
 * every track, forever, in a way that looks like a plausible fade rather than a bug.
 */
export function bucketFrameCount(
  layout: WaveformMipmapLayout,
  level: WaveformMipmapLevel,
  bucket: number,
): number {
  const start = bucket * level.bucketFrames;
  return Math.max(0, Math.min(level.bucketFrames, layout.frameCount - start));
}

/**
 * The level to draw from at a given zoom, expressed in frames per device pixel.
 *
 * Picks the **coarsest** level that still has at least one bucket per pixel: the fewest buckets
 * to read without the renderer having to invent detail. Falls back to level 0 when the caller has
 * zoomed in past the finest level, which is a legitimate state (the waveform simply goes blocky)
 * rather than an error.
 */
export function levelForFramesPerPixel(
  layout: WaveformMipmapLayout,
  framesPerPixel: number,
): WaveformMipmapLevel {
  let chosen = layout.levels[0];

  if (chosen === undefined) {
    throw new RangeError('waveform mipmap: layout has no levels');
  }

  for (const level of layout.levels) {
    if (level.bucketFrames <= framesPerPixel) {
      chosen = level;
    }
  }

  return chosen;
}

/** True when bit `level` is set in a `LEVELS_READY` mask. */
export function isLevelReady(readyMask: number, level: number): boolean {
  return (readyMask & (1 << level)) !== 0;
}

/** `readyMask` with bit `level` set. Kept beside `isLevelReady` so the two cannot disagree. */
export function withLevelReady(readyMask: number, level: number): number {
  return readyMask | (1 << level);
}
